import { useState } from 'react';
import type { JobTaskView } from '@codebox/shared';

/**
 * The live task checklist (SPEC-P1-TASKLIST §6) — what the AI said it would do, and how far it has
 * got. Purely presentational: StudioPage owns the socket subscription, the hydration fetch and the
 * forward-only merge, so this component can be dropped anywhere the same data is available.
 *
 * Why it exists: a build used to be a single opaque spinner with a rotating phase name. The
 * checklist makes the plan itself visible BEFORE the work happens, which is what turns "is it stuck?"
 * into "it is on step 3 of 6".
 */

const ICON: Record<JobTaskView['status'], string> = {
  planned: '',
  running: '',
  done: '✓',
  failed: '✕',
  skipped: '–',
};

function StatusDot({ status }: { status: JobTaskView['status'] }) {
  const base = 'mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full text-[10px] font-bold';
  if (status === 'running') {
    /*
     * A spinning ring, not a pulsing dot. Steps can sit on the same row for 20s+ while the model
     * writes a file, and a pulse reads as "nothing is happening" — the checklist looked frozen even
     * though it was working. Rotation is the one cue that unambiguously says "still going".
     */
    return (
      <span className={`${base} text-accent`} role="status" aria-label="in progress">
        <svg width="16" height="16" viewBox="0 0 16 16" className="animate-spin" aria-hidden="true">
          <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.5" />
          <path d="M8 2a6 6 0 0 1 6 6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  if (status === 'done') {
    // scales in so the swap from spinner to tick reads as the step completing
    return (
      <span className={`${base} anim-pop bg-accent text-accent-ink`} aria-label="done">
        {ICON.done}
      </span>
    );
  }
  if (status === 'failed') {
    return <span className={`${base} bg-danger/20 text-danger`} aria-label="did not finish">{ICON.failed}</span>;
  }
  if (status === 'skipped') {
    return <span className={`${base} border border-line text-muted`} aria-label="skipped">{ICON.skipped}</span>;
  }
  return <span className={`${base} border border-line`} aria-label="not started yet" />;
}

export interface TaskListPanelProps {
  tasks: JobTaskView[];
  /** Sparky line for the "now doing" strip; falls back to the running task's title. */
  narration?: string | null;
  /** true once the job finished — the panel collapses but stays visible until the next run. */
  collapsed?: boolean;
  /** when floating over the workspace, give it a way out that is not the work bubble */
  onClose?: () => void;
  className?: string;
}

export function TaskListPanel({ tasks, narration, collapsed = false, onClose, className }: TaskListPanelProps) {
  const [open, setOpen] = useState(true);
  const [showDetail, setShowDetail] = useState<string | null>(null);
  if (tasks.length === 0) return null;

  const running = tasks.find(t => t.status === 'running');
  const finished = tasks.filter(t => t.status !== 'planned' && t.status !== 'running').length;
  const nowDoing = narration ?? running?.title ?? (collapsed ? 'All done' : 'Getting started');
  const expanded = open && !collapsed;

  return (
    <section className={['rounded-card border border-line bg-surface', className ?? ''].join(' ')}>
      <div className="flex items-center gap-1 pr-2">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-[11px] font-bold uppercase tracking-wider text-muted">
              Sparky's plan · {finished}/{tasks.length}
            </span>
            <span className="mt-0.5 block truncate text-sm font-semibold">{nowDoing}</span>
          </span>
          <span className="shrink-0 text-xs text-muted">{expanded ? 'Hide' : 'Show'}</span>
        </button>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close the plan"
            title="Close"
            className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted transition hover:bg-page hover:text-ink"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
      </div>

      {expanded && (
        <ol className="space-y-1 px-2 pb-3">
          {tasks.map(task => {
            const isRunning = task.status === 'running';
            const isPending = task.status === 'planned';
            return (
              <li
                key={task.id}
                className={[
                  'flex items-start gap-3 rounded-xl px-3 py-2 transition',
                  isRunning ? 'bg-accent/10' : isPending ? 'opacity-50' : 'bg-page',
                ].join(' ')}
              >
                <StatusDot status={task.status} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{task.title}</span>
                  {task.detail && (
                    showDetail === task.id ? (
                      <span className="block text-xs text-muted">{task.detail}</span>
                    ) : (
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setShowDetail(task.id);
                        }}
                        className="text-xs text-muted underline-offset-2 hover:underline"
                      >
                        What's this?
                      </button>
                    )
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
